import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import toast from 'react-hot-toast';
import useCartStore from '../store/useCartStore';
import useAuthStore from '../store/useAuthStore';
import { KurtiPlaceholder } from '../components/ProductCard';
import { FiTrash2, FiPlus, FiMinus, FiTag, FiShoppingBag, FiCheck } from 'react-icons/fi';
import { AuroraBlobs, GrainOverlay, GlassChip, MagneticButton, Reveal, fadeUp, stagger } from '../components/ui';

export default function CartPage() {
  const { cart, totals, fetchCart, updateQty, removeItem, applyCoupon, removeCoupon } = useCartStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [couponCode, setCouponCode] = useState('');
  const [applying, setApplying] = useState(false);

  useEffect(() => { if (user) fetchCart(); }, [user]);

  if (!user) return <EmptyState icon={<FiShoppingBag size={42} />} title="Sign in to view your bag" body="Track your saved pieces and place orders securely." cta={{ to: '/login', label: 'Sign in' }} />;

  const items = cart?.items || [];
  if (items.length === 0) return <EmptyState icon={<FiShoppingBag size={42} />} title="Your bag is empty" body="Find your vritiethnic — every kurta starts a story." cta={{ to: '/', label: 'Browse the shop' }} />;

  return (
    <div>
      {/* Hero strip */}
      <section style={{ position: 'relative', background: '#1A100E', overflow: 'hidden', padding: '48px 24px 40px' }}>
        <AuroraBlobs muted />
        <GrainOverlay />
        <div style={{ position: 'relative', zIndex: 2, maxWidth: 1100, margin: '0 auto' }}>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}>
            <GlassChip>Your bag</GlassChip>
          </motion.div>
          <motion.h1 initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, delay: 0.1 }} className="serif" style={{ fontSize: 'clamp(34px, 4.4vw, 52px)', color: '#FFF', lineHeight: 1, letterSpacing: '-0.02em', marginTop: 18, marginBottom: 10 }}>
            {items.length} piece{items.length > 1 ? 's' : ''} ready
          </motion.h1>
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay: 0.25 }} style={{ fontSize: 14, color: 'rgba(237,227,209,0.7)' }}>
            Review and proceed when you're ready.
          </motion.div>
        </div>
      </section>

      <div style={{ maxWidth: 1100, margin: '0 auto', padding: '40px 24px 64px' }}>
        <div className="cart-grid" style={{ display: 'grid', gridTemplateColumns: '1fr 380px', gap: 32, alignItems: 'flex-start' }}>
          {/* Items */}
          <motion.div variants={stagger} initial="hidden" animate="show" style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
            <AnimatePresence mode="popLayout">
              {items.map(item => (
                <motion.div
                  key={item._id}
                  layout
                  variants={fadeUp}
                  initial="hidden"
                  animate="show"
                  exit={{ opacity: 0, x: -32, transition: { duration: 0.25 } }}
                  whileHover={{ y: -2 }}
                  style={{
                    background: 'rgba(255,255,255,0.65)',
                    border: '1px solid rgba(229,219,200,0.6)',
                    borderRadius: 18,
                    backdropFilter: 'blur(20px) saturate(160%)',
                    WebkitBackdropFilter: 'blur(20px) saturate(160%)',
                    padding: 18,
                    display: 'grid', gridTemplateColumns: '108px 1fr auto', gap: 18,
                    boxShadow: '0 4px 12px rgba(42,31,26,0.04)',
                  }}
                >
                  <div style={{ width: 108, height: 140, borderRadius: 12, overflow: 'hidden', background: 'var(--surface-2)' }}>
                    {item.product?.images?.[0]?.url
                      ? <img src={item.product.images[0].url} alt={item.product.name} style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                      : <KurtiPlaceholder color={item.color?.hex || '#7A1F2B'} />}
                  </div>
                  <div style={{ minWidth: 0 }}>
                    <div style={{ fontSize: 10, color: 'var(--gold)', fontWeight: 700, letterSpacing: '0.16em', textTransform: 'uppercase', marginBottom: 6 }}>VritiEthnic</div>
                    <Link to={`/product/${item.product?.slug}`} className="serif" style={{ fontSize: 19, color: 'var(--ink)', textDecoration: 'none', lineHeight: 1.2, display: 'block', marginBottom: 8 }}>
                      {item.product?.name}
                    </Link>
                    <div style={{ fontSize: 12, color: 'var(--muted)', marginBottom: 12, display: 'flex', alignItems: 'center', gap: 8 }}>
                      Size {item.size}
                      {item.color?.hex && (
                        <>
                          <span style={{ width: 3, height: 3, borderRadius: '50%', background: 'var(--line)' }} />
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                            <span style={{ width: 12, height: 12, borderRadius: '50%', background: item.color.hex, border: '1px solid rgba(0,0,0,0.1)' }} />
                            {item.color.name}
                          </span>
                        </>
                      )}
                    </div>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                      <div style={{ display: 'inline-flex', alignItems: 'center', border: '1px solid var(--line)', borderRadius: 999, background: 'rgba(255,255,255,0.7)' }}>
                        <motion.button onClick={() => updateQty(item._id, Math.max(1, item.qty - 1))} whileTap={{ scale: 0.85 }} style={{ width: 36, height: 36, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: 999 }}>
                          <FiMinus size={12} />
                        </motion.button>
                        <span style={{ minWidth: 28, textAlign: 'center', fontSize: 14, fontWeight: 700 }}>{item.qty}</span>
                        <motion.button onClick={() => updateQty(item._id, item.qty + 1)} whileTap={{ scale: 0.85 }} style={{ width: 36, height: 36, border: 'none', background: 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer', borderRadius: 999 }}>
                          <FiPlus size={12} />
                        </motion.button>
                      </div>
                      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
                        <span className="serif" style={{ fontSize: 18, fontWeight: 600 }}>₹{(item.product?.price * item.qty)?.toLocaleString('en-IN')}</span>
                        {item.product?.mrp > item.product?.price && (
                          <span style={{ fontSize: 12, color: 'var(--muted)', textDecoration: 'line-through' }}>₹{(item.product?.mrp * item.qty)?.toLocaleString('en-IN')}</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <motion.button onClick={() => removeItem(item._id)} whileTap={{ scale: 0.85 }} whileHover={{ color: 'var(--danger)' }} style={{ width: 36, height: 36, border: 'none', background: 'transparent', color: 'var(--muted)', display: 'flex', alignItems: 'center', justifyContent: 'flex-start', cursor: 'pointer', alignSelf: 'flex-start' }}>
                    <FiTrash2 size={16} />
                  </motion.button>
                </motion.div>
              ))}
            </AnimatePresence>
          </motion.div>

          {/* Summary */}
          <div style={{ position: 'sticky', top: 96 }}>
            {/* Coupon card */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} style={{
              background: 'rgba(255,255,255,0.65)', border: '1px solid rgba(229,219,200,0.6)', borderRadius: 16,
              backdropFilter: 'blur(20px) saturate(160%)', WebkitBackdropFilter: 'blur(20px) saturate(160%)',
              padding: 16, marginBottom: 14, display: 'flex', alignItems: 'center', gap: 12,
            }}>
              <span style={{ width: 32, height: 32, borderRadius: '50%', background: 'var(--gold-soft)', color: 'var(--gold)', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <FiTag size={14} />
              </span>
              {cart?.coupon?.code ? (
                <>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--ok)', letterSpacing: '0.06em', display: 'inline-flex', alignItems: 'center', gap: 6 }}>
                      <FiCheck size={12} /> {cart.coupon.code} applied
                    </div>
                  </div>
                  <button onClick={removeCoupon} style={{ border: '1px solid var(--danger)', background: 'transparent', color: 'var(--danger)', borderRadius: 999, padding: '6px 14px', fontSize: 11, fontWeight: 700, cursor: 'pointer', letterSpacing: '0.06em', textTransform: 'uppercase' }}>Remove</button>
                </>
              ) : (
                <>
                  <input
                    value={couponCode}
                    onChange={e => setCouponCode(e.target.value.toUpperCase())}
                    placeholder="Coupon code"
                    style={{ border: 'none', padding: 0, background: 'transparent', fontSize: 14, flex: 1, textTransform: 'uppercase', letterSpacing: '0.06em', outline: 'none', fontFamily: 'inherit' }}
                  />
                  <MagneticButton variant="outline" size="sm" onClick={async () => {
                    if (!couponCode.trim()) return;
                    setApplying(true);
                    try { await applyCoupon(couponCode.trim()); toast.success('Coupon applied!'); setCouponCode(''); }
                    catch (err) { toast.error(err.message || 'Invalid coupon'); }
                    setApplying(false);
                  }} disabled={applying}>
                    {applying ? 'Applying' : 'Apply'}
                  </MagneticButton>
                </>
              )}
            </motion.div>

            {/* Order summary card */}
            <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6, delay: 0.1 }} style={{
              background: 'rgba(255,255,255,0.7)', border: '1px solid rgba(229,219,200,0.65)', borderRadius: 20,
              backdropFilter: 'blur(24px) saturate(160%)', WebkitBackdropFilter: 'blur(24px) saturate(160%)',
              padding: 24, boxShadow: '0 12px 32px rgba(42,31,26,0.06)',
            }}>
              <div style={{ fontSize: 10, fontWeight: 700, letterSpacing: '0.2em', textTransform: 'uppercase', color: 'var(--gold)', marginBottom: 18 }}>Order Summary</div>
              {[
                { label: 'Subtotal', value: `₹${totals.subtotal?.toLocaleString('en-IN')}` },
                { label: `Discount${cart?.coupon?.code ? ` (${cart.coupon.code})` : ''}`, value: totals.discount > 0 ? `– ₹${totals.discount}` : '—', good: totals.discount > 0 },
                { label: 'Shipping', value: totals.shipping === 0 ? 'Free' : `₹${totals.shipping}`, good: totals.shipping === 0 },
                { label: 'GST', value: 'Included', muted: true },
              ].map(row => (
                <div key={row.label} style={{ display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderBottom: '1px dashed var(--line-2)' }}>
                  <span style={{ fontSize: 13, color: row.muted ? 'var(--muted)' : 'var(--ink-2)' }}>{row.label}</span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: row.good ? 'var(--ok)' : row.muted ? 'var(--muted)' : 'var(--ink)' }}>{row.value}</span>
                </div>
              ))}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', padding: '16px 0 0' }}>
                <span style={{ fontSize: 14, fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.1em' }}>Total</span>
                <span className="serif" style={{ fontSize: 28, fontWeight: 600, letterSpacing: '-0.01em' }}>₹{totals.total?.toLocaleString('en-IN')}</span>
              </div>
              {totals.discount > 0 && (
                <div style={{ fontSize: 12, color: 'var(--ok)', marginTop: 8, fontWeight: 700, padding: '8px 12px', background: 'rgba(79,122,83,0.08)', borderRadius: 10, textAlign: 'center', letterSpacing: '0.04em' }}>
                  You saved ₹{(totals.subtotal - totals.total + totals.shipping)?.toLocaleString('en-IN')}
                </div>
              )}
              <div style={{ marginTop: 22 }}>
                <MagneticButton variant="primary" size="lg" onClick={() => navigate('/checkout')} style={{ width: '100%' }}>
                  Proceed to Checkout ›
                </MagneticButton>
              </div>
              <div style={{ fontSize: 11, color: 'var(--muted)', textAlign: 'center', marginTop: 12 }}>
                Secure checkout · Razorpay & COD
              </div>
            </motion.div>
          </div>
        </div>
      </div>

      <style>{`
        @media (max-width: 900px) {
          .cart-grid { grid-template-columns: 1fr !important; }
        }
      `}</style>
    </div>
  );
}

function EmptyState({ icon, title, body, cta }) {
  return (
    <div style={{ minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '60px 24px' }}>
      <Reveal style={{ textAlign: 'center', maxWidth: 420 }}>
        <div style={{ display: 'inline-flex', width: 80, height: 80, borderRadius: '50%', background: 'var(--gold-soft)', color: 'var(--gold)', alignItems: 'center', justifyContent: 'center', marginBottom: 20 }}>
          {icon}
        </div>
        <h2 className="serif" style={{ fontSize: 32, marginBottom: 10, color: 'var(--ink)' }}>{title}</h2>
        <p style={{ fontSize: 14, color: 'var(--muted)', marginBottom: 24, lineHeight: 1.7 }}>{body}</p>
        <MagneticButton to={cta.to} variant="primary">{cta.label}</MagneticButton>
      </Reveal>
    </div>
  );
}
